import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Upload, Trash2, Search, Check } from "lucide-react";
import { uploadMany } from "@/lib/uploads";

const PRIORITIES = [
  { key: "low", label: "Low" },
  { key: "medium", label: "Medium" },
  { key: "high", label: "High" },
];

export default function RaiseTicketModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [priority, setPriority] = useState("medium");
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState([]);
  const [projectQ, setProjectQ] = useState("");
  const [stakeholders, setStakeholders] = useState({ project_users: [], others: [] });
  const [taggedIds, setTaggedIds] = useState([]);
  const [userQ, setUserQ] = useState("");
  const [files, setFiles] = useState([]);      // File objects staged locally
  const [uploaded, setUploaded] = useState([]); // uploaded file_ids
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tickets/my-projects");
        setProjects(data.projects || []);
      } catch { /* silent */ }
    })();
  }, []);

  const loadStakeholders = useCallback(async () => {
    try {
      const url = projectId
        ? `/tickets/stakeholders?project_id=${encodeURIComponent(projectId)}`
        : "/tickets/stakeholders";
      const { data } = await api.get(url);
      setStakeholders(data || { project_users: [], others: [] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, [projectId]);
  useEffect(() => { loadStakeholders(); }, [loadStakeholders]);

  const toggleTag = (uid) => {
    setTaggedIds((cur) => cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid]);
  };

  const onPickFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    setFiles((cur) => [...cur, ...list]);
    setBusy(true);
    try {
      const results = await uploadMany(list, undefined, "ticket_attachment");
      setUploaded((cur) => [...cur, ...results.map((r) => r.id)]);
      toast.success(`${results.length} file${results.length === 1 ? "" : "s"} attached`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
      setFiles((cur) => cur.filter((f) => !list.includes(f)));
    } finally { setBusy(false); }
  };

  const removeFile = (idx) => {
    setFiles((cur) => cur.filter((_, i) => i !== idx));
    setUploaded((cur) => cur.filter((_, i) => i !== idx));
  };

  const filteredProjects = useMemo(() => {
    const term = projectQ.trim().toLowerCase();
    if (!term) return projects.slice(0, 100);
    return projects.filter((p) =>
      (p.client_name || "").toLowerCase().includes(term)
      || (p.project_name || "").toLowerCase().includes(term)
    ).slice(0, 100);
  }, [projects, projectQ]);

  const filterUsers = (arr) => {
    const term = userQ.trim().toLowerCase();
    if (!term) return arr;
    return arr.filter((u) =>
      (u.name || "").toLowerCase().includes(term)
      || (u.email || "").toLowerCase().includes(term)
      || (u.role_name || "").toLowerCase().includes(term)
    );
  };

  const submit = async () => {
    if (title.trim().length < 3) {
      toast.error("Title must be at least 3 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/tickets", {
        title: title.trim(),
        description: desc.trim(),
        priority,
        project_id: projectId || null,
        tagged_user_ids: taggedIds,
        attachment_file_ids: uploaded,
      });
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to raise ticket");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="raise-ticket-modal">
      <div className="bg-[#0a0a0a] border border-[#262626] rounded-lg w-full max-w-4xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#262626]">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Raise Ticket</p>
            <h3 className="font-display text-xl text-white mt-0.5">New Internal Ticket</h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="raise-ticket-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-5">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Title *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="Short summary of the issue"
                   className="iz-input w-full" data-testid="ticket-title-input"/>
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Description</label>
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)}
                      rows={4} placeholder="What’s happening? What did you expect?"
                      className="iz-input w-full" data-testid="ticket-desc-input"/>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Priority</label>
              <div className="flex gap-2" data-testid="ticket-priority-group">
                {PRIORITIES.map((p) => (
                  <button key={p.key} onClick={() => setPriority(p.key)}
                          className={`flex-1 text-xs font-mono uppercase tracking-widest px-3 py-2 rounded border transition-colors ${
                            priority === p.key
                              ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/10"
                              : "border-[#262626] text-gray-500 hover:text-white"
                          }`}
                          data-testid={`ticket-priority-${p.key}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">
                Project (optional)
              </label>
              <div className="relative">
                <input value={projectQ}
                       onChange={(e) => { setProjectQ(e.target.value); setProjectId(""); }}
                       placeholder="Search your projects…"
                       className="iz-input w-full"
                       data-testid="ticket-project-search"/>
              </div>
              {projectQ && !projectId && (
                <div className="mt-1 max-h-40 overflow-y-auto border border-[#262626] rounded-md bg-[#050505]"
                     data-testid="ticket-project-suggestions">
                  {filteredProjects.length === 0 && (
                    <p className="p-2 text-xs text-gray-500 italic">No projects match.</p>
                  )}
                  {filteredProjects.map((p) => (
                    <button key={p.id} onClick={() => {
                              setProjectId(p.id);
                              setProjectQ(`${p.client_name || ""} · ${p.project_name || ""}`);
                            }}
                            className="w-full text-left text-xs px-3 py-1.5 hover:bg-[#111] text-gray-300"
                            data-testid={`ticket-project-option-${p.id}`}>
                      <span className="text-white">{p.client_name || "—"}</span>
                      {p.project_name ? <span className="text-gray-500"> · {p.project_name}</span> : null}
                      {p.city ? <span className="text-gray-600"> · {p.city}</span> : null}
                    </button>
                  ))}
                </div>
              )}
              {projectId && (
                <button onClick={() => { setProjectId(""); setProjectQ(""); }}
                        className="mt-1 text-[10px] text-gray-500 hover:text-white"
                        data-testid="ticket-project-clear">
                  Clear project
                </button>
              )}
            </div>
          </div>

          {/* Stakeholder picker */}
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">
              Tag Stakeholders ({taggedIds.length} selected)
            </label>
            <div className="flex items-center gap-2 border border-[#262626] rounded-md px-2 py-1 bg-[#050505] mb-2">
              <Search className="w-3.5 h-3.5 text-gray-600"/>
              <input value={userQ} onChange={(e) => setUserQ(e.target.value)}
                     placeholder="Search by name, email or role"
                     className="bg-transparent outline-none text-xs text-white w-full"
                     data-testid="ticket-tag-search"/>
            </div>
            <div className="max-h-60 overflow-y-auto border border-[#262626] rounded-md">
              {stakeholders.project_users.length > 0 && (
                <StakeholderGroup title="Project stakeholders"
                                   users={filterUsers(stakeholders.project_users)}
                                   taggedIds={taggedIds}
                                   onToggle={toggleTag}
                                   groupTestid="ticket-stakeholders-project"/>
              )}
              <StakeholderGroup title={stakeholders.project_users.length ? "Everyone else" : "All users"}
                                 users={filterUsers(stakeholders.others)}
                                 taggedIds={taggedIds}
                                 onToggle={toggleTag}
                                 groupTestid="ticket-stakeholders-others"/>
            </div>
          </div>

          {/* Attachments */}
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">
              Attachments
            </label>
            <label className="inline-flex items-center gap-1.5 iz-btn-secondary text-xs cursor-pointer"
                   data-testid="ticket-attach-btn">
              <Upload className="w-3.5 h-3.5"/> Attach files
              <input type="file" multiple hidden onChange={onPickFiles}
                     accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                     data-testid="ticket-attach-input"/>
            </label>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1" data-testid="ticket-attach-list">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`}
                      className="flex items-center gap-2 text-xs text-gray-300 border border-[#1a1a1a] rounded px-2 py-1"
                      data-testid={`ticket-attach-item-${i}`}>
                    <span className="flex-1 truncate">{f.name}</span>
                    <button onClick={() => removeFile(i)} className="text-gray-500 hover:text-red-400"
                            data-testid={`ticket-attach-remove-${i}`}>
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="border-t border-[#262626] px-5 py-3 flex items-center justify-end gap-2">
          <button onClick={onClose} className="iz-btn-secondary text-xs"
                  data-testid="ticket-cancel-btn">Cancel</button>
          <button onClick={submit} disabled={busy || !title.trim()}
                  className="iz-btn-primary text-xs flex items-center gap-1.5"
                  data-testid="ticket-submit-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Check className="w-3.5 h-3.5"/>}
            Raise Ticket
          </button>
        </div>
      </div>
    </div>
  );
}

function StakeholderGroup({ title, users, taggedIds, onToggle, groupTestid }) {
  return (
    <div data-testid={groupTestid}>
      <div className="px-3 py-1.5 bg-[#111] text-[10px] font-mono uppercase tracking-widest text-gray-500 sticky top-0">
        {title} · {users.length}
      </div>
      {users.length === 0 && (
        <p className="px-3 py-2 text-xs text-gray-600 italic">No users match.</p>
      )}
      {users.map((u) => {
        const on = taggedIds.includes(u.id);
        return (
          <button key={u.id} onClick={() => onToggle(u.id)}
                  className={`w-full text-left text-xs px-3 py-2 border-b border-[#161616] flex items-center gap-2 ${
                    on ? "bg-[#d4af37]/10" : "hover:bg-[#111]"
                  }`}
                  data-testid={`ticket-tag-${u.id}`}>
            <span className={`w-3 h-3 rounded-sm border ${on ? "bg-[#d4af37] border-[#d4af37]" : "border-[#333]"}`}></span>
            <span className="text-white flex-1 truncate">{u.name || "—"}</span>
            <span className="text-gray-500 text-[10px]">{u.role_name || ""}</span>
          </button>
        );
      })}
    </div>
  );
}
